<script setup lang="ts">
import { watch } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import DatePicker from '../../Shared/DatePicker.vue'

const props = defineProps<{
  disable?: boolean
}>()

const { umsokn } = useUmsokn()

const rules = {
  umsokn: {
    tryggingarstofnunFrom: { required: requiredIf(() => umsokn.value?.tryggingarstofnun === 'Já') },
    tryggingarstofnunTo:   { required: requiredIf(() => umsokn.value?.tryggingarstofnun === 'Já') },

    stettarfelagName:   { required: requiredIf(() => umsokn.value?.stettarfelag === 'Já') },
    stettarfelagFrom:   { required: requiredIf(() => umsokn.value?.stettarfelag === 'Já') },
    stettarfelagTo:     { required: requiredIf(() => umsokn.value?.stettarfelag === 'Já') },
    stettarfelagAmount: { required: requiredIf(() => umsokn.value?.stettarfelag === 'Já') },

    otherWho:    { required: requiredIf(() => umsokn.value?.otherPayments === 'Já') },
    otherFrom:   { required: requiredIf(() => umsokn.value?.otherPayments === 'Já') },
    otherTo:     { required: requiredIf(() => umsokn.value?.otherPayments === 'Já') },
    otherAmount: { required: requiredIf(() => umsokn.value?.otherPayments === 'Já') }
  }
}

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  }
})

function todayISO() {
  return new Date().toISOString().split('T')[0]
}

// format amounts
watch(
  () => umsokn.value?.otherAmount,
  newVal => {
    if (newVal) {
      const digits = newVal.replace(/\D/g, '')
      umsokn.value.otherAmount = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    }
  }
)

watch(
  () => umsokn.value?.stettarfelagAmount,
  newVal => {
    if (newVal) {
      const digits = newVal.replace(/\D/g, '')
      umsokn.value.stettarfelagAmount = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    }
  }
)
</script>

<template>
  <div v-if="umsokn">
    <v-row>
      <v-col>
        <h2 class="form__header">{{ $t('ororku_umsokn_step4_header') }}</h2>
        <p style="font-weight:bold">{{ $t('ororku_umsokn_step4_header2') }}</p>
      </v-col>
    </v-row>

    <!-- Tryggingarstofnun -->
    <v-row>
      <v-col cols="12">
        <p>{{ $t('ororku_umsokn_step4_trygginastofnun') }}</p>
        <v-radio-group v-model="umsokn.tryggingarstofnun" :readonly="props.disable" inline hide-details>
          <v-radio :label="$t('ororku_umsokn_step4_trygginastofnun_yes')" value="Já" />
          <v-radio :label="$t('ororku_umsokn_step4_trygginastofnun_no')" value="Nei" />
          <v-radio :label="$t('ororku_umsokn_step4_haveapplication_have')" value="Hef umsókn" />
        </v-radio-group>
      </v-col>
    </v-row>

    <v-row v-if="umsokn.tryggingarstofnun === 'Já'">
      <v-col cols="12" md="4">
        <DatePicker
          v-model="umsokn.tryggingarstofnunFrom"
          :label="$t('ororku_umsokn_step4_from')"
          :disabled="props.disable"
          :max="todayISO()"
          :error="v$.umsokn.tryggingarstofnunFrom.$error"
          :error-messages="v$.umsokn.tryggingarstofnunFrom.$error ? [$t('ororku_umsokn_step4_tryggingarstofnunFrom_error')] : []"
        />
      </v-col>
      <v-col cols="12" md="4">
        <DatePicker
          v-model="umsokn.tryggingarstofnunTo"
          :label="$t('ororku_umsokn_step4_to')"
          :disabled="props.disable"
          :max="todayISO()"
          :error="v$.umsokn.tryggingarstofnunTo.$error"
          :error-messages="v$.umsokn.tryggingarstofnunTo.$error ? [$t('ororku_umsokn_step4_tryggingarstofnunTo_error')] : []"
        />
      </v-col>
    </v-row>

    <!-- Stéttarfélag -->
    <v-row>
      <v-col cols="12">
        <p>{{ $t('ororku_umsokn_step4_sjukrasjodur') }}</p>
        <v-radio-group v-model="umsokn.stettarfelag" :readonly="props.disable" inline hide-details>
          <v-radio :label="$t('ororku_umsokn_step4_sjukrasjodur_yes')" value="Já" />
          <v-radio :label="$t('ororku_umsokn_step4_sjukrasjodur_no')" value="Nei" />
        </v-radio-group>
      </v-col>
    </v-row>

    <div v-if="umsokn.stettarfelag === 'Já'">
      <v-row>
        <v-col cols="12" md="4">
          <DatePicker
            v-model="umsokn.stettarfelagFrom"
            :label="$t('ororku_umsokn_step4_from')"
            :disabled="props.disable"
            :max="todayISO()"
            :error="v$.umsokn.stettarfelagFrom.$error"
            :error-messages="v$.umsokn.stettarfelagFrom.$error ? [$t('ororku_umsokn_step4_stettarfelagFrom_error')] : []"
          />
        </v-col>
        <v-col cols="12" md="4">
          <DatePicker
            v-model="umsokn.stettarfelagTo"
            :label="$t('ororku_umsokn_step4_to')"
            :disabled="props.disable"
            :max="todayISO()"
            :error="v$.umsokn.stettarfelagTo.$error"
            :error-messages="v$.umsokn.stettarfelagTo.$error ? [$t('ororku_umsokn_step4_stettarfelagTo_error')] : []"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="umsokn.stettarfelagName"
            :readonly="props.disable"
             variant="outlined"
            density="comfortable"
            :label="$t('ororku_umsokn_step4_sjukrasjodur_felag')"
            :error="v$.umsokn.stettarfelagName.$error"
            :error-messages="v$.umsokn.stettarfelagName.$error ? [$t('ororku_umsokn_step4_stettarfelagName_error')] : []"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="umsokn.stettarfelagAmount"
            :readonly="props.disable"
             variant="outlined"
            density="comfortable"
            :label="$t('ororku_umsokn_step4_sjukrasjodur_felag_upphaed')"
            :error="v$.umsokn.stettarfelagAmount.$error"
            :error-messages="v$.umsokn.stettarfelagAmount.$error ? [$t('ororku_umsokn_step4_stettarfelagAmount_error')] : []"
          />
        </v-col>
      </v-row>
    </div>

    <!-- Other payments -->
    <v-row>
      <v-col cols="12">
        <p>{{ $t('ororku_umsokn_step4_other_payment') }}</p>
        <v-radio-group v-model="umsokn.otherPayments" :readonly="props.disable" inline hide-details>
          <v-radio :label="$t('ororku_umsokn_step4_other_payment_yes')" value="Já" />
          <v-radio :label="$t('ororku_umsokn_step4_other_payment_no')" value="Nei" />
        </v-radio-group>
      </v-col>
    </v-row>

    <div v-if="umsokn.otherPayments === 'Já'">
      <v-row>
        <v-col cols="12" md="4">
          <DatePicker
            v-model="umsokn.otherFrom"
            :label="$t('ororku_umsokn_step4_from')"
            :disabled="props.disable"
            :max="todayISO()"
            :error="v$.umsokn.otherFrom.$error"
            :error-messages="v$.umsokn.otherFrom.$error ? [$t('ororku_umsokn_step4_otherFrom_error')] : []"
          />
        </v-col>
        <v-col cols="12" md="4">
          <DatePicker
            v-model="umsokn.otherTo"
            :label="$t('ororku_umsokn_step4_to')"
            :disabled="props.disable"
            :max="todayISO()"
            :error="v$.umsokn.otherTo.$error"
            :error-messages="v$.umsokn.otherTo.$error ? [$t('ororku_umsokn_step4_otherTo_error')] : []"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="umsokn.otherWho"
            variant="outlined"
            density="comfortable"
            :readonly="props.disable"
            :label="$t('ororku_umsokn_step4_other_payment_who')"
            :error="v$.umsokn.otherWho.$error"
            :error-messages="v$.umsokn.otherWho.$error ? [$t('ororku_umsokn_step4_otherWho_error')] : []"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="umsokn.otherAmount"
            variant="outlined"
            density="comfortable"
            :readonly="props.disable"
            :label="$t('ororku_umsokn_step4_other_payment_upphaed')"
            :error="v$.umsokn.otherAmount.$error"
            :error-messages="v$.umsokn.otherAmount.$error ? [$t('ororku_umsokn_step4_otherAmount_error')] : []"
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>
